<template>
  <div class="etle-list-container py-4 px-3 px-md-4">

    <!-- State 1: Loading -->
    <div v-if="loading" class="state-card text-center py-5 shadow-sm rounded-4 bg-white">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="text-muted fw-semibold mb-0">Sedang memuat data pelanggaran...</p>
    </div>

    <!-- State 2: Error -->
    <div v-else-if="error" class="error-card p-4 shadow-sm rounded-4 bg-white border-start border-danger border-4">
      <div class="d-flex align-items-center mb-3">
        <i class="bi bi-exclamation-octagon-fill text-danger fs-2 me-3"></i>
        <div>
          <h5 class="mb-0 fw-bold text-danger">Terjadi Kesalahan</h5>
          <small class="text-muted">Gagal mengambil data dari server</small>
        </div>
      </div>
      <p class="text-secondary fs-7 mb-4">{{ error }}</p>
      <div class="d-flex gap-2">
        <button class="btn btn-navy-action btn-sm px-3" @click="retryFetch">
          <i class="bi bi-arrow-counterclockwise me-1"></i> Coba Lagi
        </button>
        <button class="btn btn-outline-secondary btn-sm px-3" @click="goBack">
          <i class="bi bi-arrow-left me-1"></i> Kembali ke Pencarian
        </button>
      </div>
    </div>

    <!-- State 3: Data Loaded -->
    <div v-else class="list-content-wrapper">

      <!-- Page Header Banner -->
      <div class="header-card mb-4 p-4 rounded-4 text-white shadow-sm">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h4 class="fw-bold mb-1 d-flex align-items-center gap-2">
              <i class="bi bi-shield-exclamation"></i> Data Pelanggaran ETLE HUB
            </h4>
            <p class="mb-0 opacity-75 fs-7">
              Daftar catatan pelanggaran lalu lintas yang terdeteksi untuk kendaraan Anda
            </p>
          </div>
          <button class="btn btn-light-custom btn-sm" @click="goBack">
            <i class="bi bi-arrow-left me-1"></i> Cari Kendaraan Lain
          </button>
        </div>
      </div>

      <!-- State: No Data Available -->
      <div v-if="!localPelanggaranData || localPelanggaranData.length === 0"
        class="empty-card text-center py-5 shadow-sm rounded-4 bg-white">
        <div class="icon-circle bg-success-subtle text-success mx-auto mb-3">
          <i class="bi bi-check-circle-fill fs-1"></i>
        </div>
        <h4 class="fw-bold text-dark">Tidak Ada Pelanggaran</h4>
        <p class="text-muted fs-7 mb-4">Selamat! Tidak ada catatan pelanggaran ETLE yang aktif untuk kendaraan ini.</p>
        <button class="btn btn-navy-action px-4 py-2" @click="goBack">
          <i class="bi bi-search me-1"></i> Kembali ke Pencarian
        </button>
      </div>

      <!-- Violation List Items -->
      <div v-else class="violation-list d-flex flex-column gap-3">
        <div v-for="(item, index) in localPelanggaranData" :key="index"
          class="violation-item-card bg-white p-3 p-md-4 rounded-4 shadow-sm border">
          <div class="row align-items-center g-3">

            <!-- Date & Time Badge -->
            <div class="col-12 col-md-3 col-lg-2">
              <div class="date-badge p-2 text-center rounded-3">
                <i class="bi bi-calendar-event me-1"></i>
                <span class="d-block fw-bold fs-7">{{ formatDate(item.inserted_date) }}</span>
                <small class="text-muted fs-8">{{ formatTime(item.inserted_date) }}</small>
              </div>
            </div>

            <!-- Location & Type Info -->
            <div class="col-12 col-md-5 col-lg-6">
              <div class="mb-2">
                <span class="badge bg-secondary-subtle text-dark border me-2">
                  <i class="bi bi-card-list me-1"></i> {{ item.report_type }}
                </span>
                <span class="badge" :class="getStatusBadgeClass(item.status_pelanggaran)">
                  {{ getStatusLabel(item) }}
                </span>
              </div>
              <div class="d-flex align-items-start gap-2 text-secondary fs-7">
                <i class="bi bi-geo-alt-fill text-danger flex-shrink-0 mt-1"></i>
                <span class="fw-medium text-dark">{{ item.lokasi }}</span>
              </div>
            </div>

            <!-- Action Buttons Section -->
            <div class="col-12 col-md-4 col-lg-4 text-md-end">
              <div class="d-flex align-items-center justify-content-md-end gap-2 flex-wrap">

                <!-- Action Group: Kondisi 1 (Sudah dibayar / ND Buka Blokir) -->
                <template v-if="item.blokir_code !== null && item.status_blanko === '1'">
                  <button class="btn btn-success btn-sm d-inline-flex align-items-center gap-1 fw-semibold"
                    @click="unduhNDBukaBlokir(item)">
                    <i class="bi bi-file-earmark-arrow-down"></i> Unduh ND Buka Blokir
                  </button>
                  <button class="btn btn-outline-secondary btn-sm rounded-circle p-0 action-info-btn"
                    @click="showInfo('Silahkan unduh surat ND Buka Blokir untuk proses selanjutnya.')" title="Petunjuk">
                    <i class="bi bi-question-lg"></i>
                  </button>
                </template>

                <!-- Action Group: Kondisi 2 (Belum Dikonfirmasi) -->
                <template v-else-if="item.status_pelanggaran === '20'">
                  <button class="btn btn-navy-action btn-sm d-inline-flex align-items-center gap-1 fw-semibold"
                    @click="konfirmasiPelanggaran(item)">
                    <i class="bi bi-check-circle"></i> Konfirmasi Pelanggaran
                  </button>
                  <button class="btn btn-outline-secondary btn-sm rounded-circle p-0 action-info-btn"
                    @click="showInfo('Untuk mengkonfirmasi pelanggaran, silahkan tekan tombol Konfirmasi Pelanggaran.')"
                    title="Petunjuk">
                    <i class="bi bi-question-lg"></i>
                  </button>
                </template>

                <!-- Action Group: Kondisi 3 (Default Detail / Download PDF) -->
                <template v-else>
                  <button v-if="item.pdf_surat_tilang"
                    class="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                    @click="downloadSurat(item.pdf_surat_tilang)" title="Download Surat Tilang">
                    <i class="bi bi-file-earmark-pdf"></i> PDF
                  </button>
                  <button class="btn btn-navy-action btn-sm d-inline-flex align-items-center gap-1 fw-semibold"
                    @click="showDetail(item)">
                    <i class="bi bi-eye"></i> Detail
                  </button>
                  <button class="btn btn-outline-secondary btn-sm rounded-circle p-0 action-info-btn"
                    @click="showInfo('Anda dapat mengunduh surat tilang, dan menuju ke Posko Gakkum terdekat untuk menyelesaikan pelanggaran.')"
                    title="Petunjuk">
                    <i class="bi bi-question-lg"></i>
                  </button>
                </template>

              </div>
            </div>

          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  props: ["pelanggaranData"],
  data() {
    return {
      localPelanggaranData: [],
      loading: false,
      error: null,
      vehicleInfo: null,
    }
  },
  async mounted() {
    await this.initializeData();
  },
  methods: {
    async initializeData() {
      // Pengecekan jika props dilempar dari parent
      if (this.pelanggaranData && this.pelanggaranData.length > 0) {
        this.localPelanggaranData = this.pelanggaranData;
        this.extractVehicleInfo();
      } else {
        const savedData = this.getSavedData();
        if (savedData && savedData.data && savedData.data.length > 0) {
          this.localPelanggaranData = savedData.data;
          this.vehicleInfo = savedData.vehicleInfo;
        } else if (this.vehicleInfo) {
          // Jika data kosong tapi info kendaraan ada, coba fetch ulang
          await this.fetchDataFromAPI();
        } else {
          this.redirectToSearch();
        }
      }
    },

    // 🔴 TAMBAHAN: Helper untuk Badge Warna Status
    getStatusBadgeClass(status) {
      switch (String(status)) {
        case '20':
          return 'bg-warning text-dark'; // Belum Konfirmasi
        case '1':
        case '2':
          return 'bg-success text-white'; // Terkonfirmasi / Lunas
        default:
          return 'bg-secondary text-white';
      }
    },

    // 🔴 TAMBAHAN: Helper untuk Label Status jika API tidak mengirim status_label
    getStatusLabel(item) {
      if (item.status_label) return item.status_label;
      if (item.status_pelanggaran === '20') return 'Belum Dikonfirmasi';
      if (item.status_blanko === '1') return 'Selesai / Buka Blokir';
      return 'Proses Tilang';
    },

    // 🔴 PERBAIKAN: Format Tanggal agar aman dari microsecond string API
    formatDate(dateString) {
      if (!dateString) return '-';
      // Cleanup string tanggal jika ada microsecond panjang dari API
      const cleanDateStr = dateString.split('.')[0].replace(' ', 'T');
      const date = new Date(cleanDateStr);

      if (isNaN(date.getTime())) return dateString; // fallback jika parse gagal

      return date.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatTime(dateString) {
      if (!dateString) return '-';
      const cleanDateStr = dateString.split('.')[0].replace(' ', 'T');
      const date = new Date(cleanDateStr);

      if (isNaN(date.getTime())) return '';

      return date.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    showInfo(message) {
      Swal.fire({
        icon: 'info',
        title: 'Informasi',
        text: message,
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
    },

    extractVehicleInfo() {
      if (this.localPelanggaranData && this.localPelanggaranData.length > 0) {
        const firstItem = this.localPelanggaranData[0];
        this.vehicleInfo = {
          noPolisi: firstItem.plat_number,
          noRangka: firstItem.noRangka,
          noMesin: firstItem.noMesin
        };
        this.saveDataToSession();
      }
    },

    saveDataToSession() {
      const dataToSave = {
        data: this.localPelanggaranData,
        vehicleInfo: this.vehicleInfo,
        timestamp: new Date().getTime()
      };
      sessionStorage.setItem('pelanggaranData', JSON.stringify(dataToSave));
    },

    getSavedData() {
      try {
        const saved = sessionStorage.getItem('pelanggaranData');
        if (saved) {
          const parsedData = JSON.parse(saved);
          const now = new Date().getTime();
          const thirtyMinutes = 30 * 60 * 1000;

          if (now - parsedData.timestamp < thirtyMinutes) {
            return parsedData;
          } else {
            sessionStorage.removeItem('pelanggaranData');
          }
        }
      } catch (error) {
        console.error('Error getting saved data:', error);
        sessionStorage.removeItem('pelanggaranData');
      }
      return null;
    },

    async fetchDataFromAPI() {
      if (!this.vehicleInfo) return;

      this.loading = true;
      this.error = null;

      const url = `${process.env.VUE_APP_API_URL}/etle/admin-etle/cek-data-kendaraan.php`;

      try {
        const response = await axios.get(url, {
          params: {
            noPolisi: this.vehicleInfo.noPolisi,
            noRangka: this.vehicleInfo.noRangka,
            noMesin: this.vehicleInfo.noMesin
          },
          timeout: 30000
        });

        console.log("Response Data API:", response.data); // Debugging Log

        if (response.data && Array.isArray(response.data.data)) {
          this.localPelanggaranData = response.data.data;
          this.saveDataToSession();
        } else {
          this.localPelanggaranData = [];
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        this.error = 'Gagal memuat data pelanggaran. Silakan coba lagi.';
      } finally {
        this.loading = false;
      }
    },

    retryFetch() {
      this.fetchDataFromAPI();
    },

    redirectToSearch() {
      Swal.fire({
        icon: 'info',
        title: 'Session Expired',
        text: 'Silakan lakukan pencarian kembali',
        confirmButtonColor: '#007bff'
      }).then(() => {
        this.$router.push({ name: 'CekKendaraan' });
      });
    },

    goBack() {
      this.$router.push({ name: 'CekKendaraan' });
    },

    async showDetail(item) {
      sessionStorage.setItem('currentViolationDetail', JSON.stringify(item));

      this.$router.push({
        name: "DetailPelanggaran",
        params: {
          plat_number: item.plat_number,
          noRangka: item.noRangka,
          noMesin: item.noMesin,
        },
      });
    },

    downloadSurat(filename) {
      if (!filename) {
        Swal.fire({
          icon: 'warning',
          title: 'File Tidak Tersedia',
          text: 'Surat tilang belum tersedia',
          confirmButtonColor: '#ffc107'
        });
        return;
      }

      const url = `${process.env.VUE_APP_API_URL}/surat/${filename}`;
      window.open(url, "_blank");
    },

    unduhNDBukaBlokir(item) {
      this.prosesUnduhNDBukaBlokir(item);
    },

    async prosesUnduhNDBukaBlokir(item) {
      try {
        const url = `${process.env.VUE_APP_API_URL}/etle/admin-etle/unduh_nota_dinas.php?ref_number=${item.ref_number}&wilayah_id=${item.wilayah_id}`;
        window.open(url, "_blank");
      } catch (error) {
        console.error('Error processing ND and blockir:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Gagal memproses permintaan. Silakan coba lagi.',
          confirmButtonColor: '#dc3545'
        });
      }
    },

    konfirmasiPelanggaran(item) {
      const url = `/#/konfirmasi?ref_number=${item.ref_number}&plates=${item.plat_number}`;
      window.open(url, '_blank');
    }
  }
};
</script>

<style scoped>
/* Theme Styling */
.etle-list-container {
  max-width: 1200px;
  margin: 0 auto;
}

.header-card {
  background-color: #24378b;
}

.btn-navy-action {
  background-color: #24378b;
  color: #ffffff;
  border: none;
  transition: background-color 0.2s ease;
}

.btn-navy-action:hover {
  background-color: #1b2a6b;
  color: #ffffff;
}

.btn-light-custom {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);
}

.btn-light-custom:hover {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

/* Card Items */
.violation-item-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.violation-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08) !important;
}

.date-badge {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
}

.icon-circle {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Action Info Helper Button */
.action-info-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Typography utilities */
.fs-7 {
  font-size: 0.875rem;
}

.fs-8 {
  font-size: 0.75rem;
}
</style>